import { Box, Button, IconButton, Paper, SelectChangeEvent, Table, TableBody, TableCell, TableHead, TableRow, TextField } from '@mui/material';
import { Fragment, useMemo, useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SelectSituacao from '../Selects/Situacao';
import SelectMagnitude from '../Selects/Magnitude';
import SelectPerigo from '../Selects/Perigo';
import BoxImagens from './BoxImagens';
import type { TypeAnomalias } from '../../../../../pages/Reports/Risrot/RegisterRisrot';
import {
	getRisrotDetailByItem,
	getRisrotDynamicItems,
	getRisrotInitialSituation,
	getRisrotStructureData,
} from './risrotEstruturaTableUtils';
import type { RisrotInitialSituationSnapshot } from './risrotEstruturaTableUtils';

export interface RisrotEstruturaBaseProps {
	onUpdate: (
		estrutura: string,
		item: string,
		campo: keyof TypeAnomalias['detalhes'][0],
		valor: string
	) => void;
	onAddItem: (estrutura: string, item: string) => void;
	onRemoveNewItem: (estrutura: string, item: string) => void;
	anomalias: TypeAnomalias[];
	initialSituationSnapshot: RisrotInitialSituationSnapshot;
	onUpdateImagens: (estrutura: string, imagens: TypeAnomalias['imagens']) => void;
	disabled: boolean;
	data_inspecao: string;
	hora_fim_inspecao: string;
	isView: boolean;
}

interface RisrotEstruturaTableProps extends RisrotEstruturaBaseProps {
	structureName: string;
	fixedItems: string[];
}

export default function RisrotEstruturaTable({
	structureName,
	fixedItems,
	onUpdate,
	onAddItem,
	onRemoveNewItem,
	anomalias,
	initialSituationSnapshot,
	onUpdateImagens,
	disabled,
	data_inspecao,
	hora_fim_inspecao,
	isView,
}: RisrotEstruturaTableProps) {
	const [novoItemNome, setNovoItemNome] = useState('');
	const [mostrarInput, setMostrarInput] = useState(false);

	const structureData = useMemo(
		() => getRisrotStructureData(anomalias, structureName),
		[anomalias, structureName],
	);

	const itensDinamicos = useMemo(
		() => getRisrotDynamicItems(structureData?.detalhes, fixedItems),
		[fixedItems, structureData?.detalhes],
	);

	const handleChange = (item: string, campo: keyof TypeAnomalias['detalhes'][0]) =>
		(e: SelectChangeEvent<string>) => {
			onUpdate(structureName, item, campo, e.target.value);
		};

	function getValue(item: string, campo: keyof TypeAnomalias['detalhes'][0]) {
		const itemData = getRisrotDetailByItem(structureData?.detalhes, item);
		return itemData?.[campo] || '';
	}

	function getUlValue(item: string) {
		return getRisrotInitialSituation(initialSituationSnapshot, structureName, item);
	}

	const handleTextChange = (item: string, campo: keyof TypeAnomalias['detalhes'][0]) => (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		onUpdate(structureName, item, campo, e.target.value);
	};

	const adicionarNovoItem = () => {
		const nextItemName = novoItemNome.trim();

		if (!nextItemName) {
			return;
		}

		onAddItem(structureName, nextItemName);
		setNovoItemNome('');
		setMostrarInput(false);
	};

	const removerItem = (item: string) => {
		onRemoveNewItem(structureName, item);
	};

	const renderLinhaItem = (indice: number, nomeItem: string, isDinamico = false, uuid?: string) => (
		<Fragment key={`${nomeItem}-${indice}`}>
			<TableRow>
				<TableCell>{indice}</TableCell>
				<TableCell>
					<Box display="flex" alignItems="center" gap={1}>
						{nomeItem}
						{isDinamico && !uuid && (
							<IconButton
								size="small"
								color="error"
								onClick={() => removerItem(nomeItem)}
								disabled={disabled}
								aria-label={`Remover item ${nomeItem}`}
							>
								<DeleteIcon fontSize="small" />
							</IconButton>
						)}
					</Box>
				</TableCell>
				<TableCell>{getUlValue(nomeItem)}</TableCell>
				<TableCell>
					<SelectSituacao
						disabled={disabled}
						onChange={handleChange(nomeItem, 'situacao')}
						value={getValue(nomeItem, 'situacao') ?? ''}
					/>
				</TableCell>
				<TableCell>
					<SelectMagnitude
						disabled={disabled}
						onChange={handleChange(nomeItem, 'magnitude')}
						value={getValue(nomeItem, 'magnitude') ?? ''}
					/>
				</TableCell>
				<TableCell>
					<SelectPerigo
						disabled={disabled}
						onChange={handleChange(nomeItem, 'npa')}
						value={getValue(nomeItem, 'npa') ?? ''}
					/>
				</TableCell>
				<TableCell>
					<TextField
						disabled={disabled}
						sx={{ maxWidth: 135 }}
						onChange={handleTextChange(nomeItem, 'codigo_anomalia')}
						value={getValue(nomeItem, 'codigo_anomalia')}
					/>
				</TableCell>
			</TableRow>
			<TableRow>
				<TableCell colSpan={10}>
					<TextField
						disabled={disabled}
						onChange={handleTextChange(nomeItem, 'observacoes_gerais')}
						label='Observação'
						fullWidth
						value={getValue(nomeItem, 'observacoes_gerais') ?? ''}
					/>
				</TableCell>
			</TableRow>
		</Fragment>
	);

	return (
		<Box padding={2} component={Paper} borderRadius={2}>
			<Table>
				<TableHead>
					<TableRow>
						<TableCell>Índice</TableCell>
						<TableCell>Item</TableCell>
						<TableCell>UL</TableCell>
						<TableCell>Situação</TableCell>
						<TableCell>Magnitude</TableCell>
						<TableCell>NPA</TableCell>
						<TableCell>Cód. Anomalia</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{fixedItems.map((item, index) => renderLinhaItem(index + 1, item))}
					{itensDinamicos.map((item, index) =>
						renderLinhaItem(fixedItems.length + index + 1, item.item, true, item.uuid),
					)}
					{mostrarInput && (
						<TableRow>
							<TableCell colSpan={10}>
								<Box display="flex" gap={2} alignItems="center">
									<TextField
										label='Nome do novo item'
										value={novoItemNome}
										onChange={(e) => setNovoItemNome(e.target.value)}
										fullWidth
										disabled={disabled}
										onKeyDown={(e) => {
											if (e.key === 'Enter') {
												e.preventDefault();
												adicionarNovoItem();
											}
										}}
									/>
									<Button
										variant='contained'
										onClick={adicionarNovoItem}
										disabled={disabled || !novoItemNome.trim()}
									>
										Confirmar
									</Button>
									<Button
										variant='outlined'
										onClick={() => {
											setMostrarInput(false);
											setNovoItemNome('');
										}}
										disabled={disabled}
									>
										Cancelar
									</Button>
								</Box>
							</TableCell>
						</TableRow>
					)}
					<TableRow>
						<TableCell colSpan={10}>
							<Button
								variant='contained'
								startIcon={<AddIcon />}
								onClick={() => setMostrarInput(true)}
								disabled={disabled || mostrarInput}
								fullWidth
							>
								Adicionar Novo Item
							</Button>
						</TableCell>
					</TableRow>
				</TableBody>
			</Table>

			<Box>
				<BoxImagens
					estrutura={structureName}
					disabled={isView}
					imagens={structureData?.imagens || []}
					onUpdateImagens={onUpdateImagens}
					data_inspecao={data_inspecao}
					hora_fim_inspecao={hora_fim_inspecao}
				/>
			</Box>
		</Box>
	);
}
